import { useRef } from "react";
import { homerunnerApiBaseUrl } from "../../hr";
import { AvailabilityDataRecord } from "../../types";
import { extractApiErrorMessage } from "../../common/utils";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import z from "zod";

export type PmsSourceName =
    | "guesty"
    | "hospitable"
    | "hostaway"
    | "hostfully"
    | "hostfully-v3"
    | "hostfully-partner"
    | "lodgify"
    | "ownerrez"
    | "streamlinevrs";

export interface Quote {
    id: string;
    property_id: number;
    status: "draft" | "pending" | "confirmed" | "expired" | "cancelled";
    checkin: string; // YYYY-MM-DD
    checkout: string; // YYYY-MM-DD
    guests: number;
    adults: number;
    children: number;
    infants: number;
    pets: number;
    nights: number;
    ip_address: string;
    rateplans: Array<{
        id: string;
        title: string;
        description: string;
        total: number;
        subTotal: number;
        totalRent: number;
        totalFees: number;
        totalTaxes: number;
        discount: number;
        securityDeposit: number;
        invoiceItems: Array<{
            title: string;
            amount: number;
            type:
            | "ACCOMMODATION_FARE"
            | "EXTRA_GUESTS_FEE"
            | "EXTRA_GUESTS_FARE"
            | "DISCOUNT"
            | "COUPON_DISCOUNT"
            | "STAY_DISCOUNT"
            | "PROMOTION"
            | "TAX"
            | "CLEANING_FEE"
            | "CLEANING_FEE_TAX"
            | "FEE"
            | "FEE_TAX"
            | "SERVICE_FEE"
            | "MANUAL"
            | "ADDON"
            | "SECURITY_DEPOSIT";
        }>;
    }>;
    coupons: null;
    /**
     * Add-ons ON this quote, in whatever shape the integration stored —
     * Guesty writes `[{id, quantity}]`, Hostaway and Hostfully a flat `[id]`.
     * Read `selected_addons` instead; this is kept because the WP plugin does.
     */
    addons?: unknown[] | null;
    /** The same selection in one shape (central#3400). */
    selected_addons?: Array<{ id: number; quantity: number }> | null;
    source: PmsSourceName;
    created_at: string; // ISO string
    updated_at: string; // ISO string
    property?: {
        title: string;
        slug?: string;
    } | null;
}

/**
 * 
 * {
    "id": "9f2ec9f5-4bc5-492a-bb32-097e737c8da2",
    "property_id": 1614,
    "referrer_id": null,
    "user_id": null,
    "status": "reserved",
    "checkin": "2025-06-22",
    "checkout": "2025-06-29",
    "guests": 1,
    "adults": 1,
    "children": 0,
    "infants": 0,
    "pets": 0,
    "nights": 7,
    "ip_address": "118.179.52.25",
    "rateplans": [
        {
            "id": "default",
            "title": "Default",
            "description": "Default",
            "total": 4576.6,
            "subTotal": 4212,
            "totalRent": 4212,
            "totalFees": 350.6,
            "totalTaxes": 14,
            "discount": 0,
            "securityDeposit": 0,
            "invoiceItems": [
                {
                    "title": "Accommodation fare",
                    "amount": 4212,
                    "type": "ACCOMMODATION_FARE"
                },
                {
                    "title": "Accommodation tax",
                    "amount": 0,
                    "type": "TAX"
                },
                {
                    "title": "Superior Interior Fee",
                    "amount": 210.6,
                    "type": "FEE"
                },
                {
                    "title": "Beach Access Fee",
                    "amount": 140,
                    "type": "FEE"
                },
                {
                    "title": "Beach Access Fee Tax",
                    "amount": 14,
                    "type": "FEE_TAX"
                }
            ]
        }
    ],
    "coupons": null,
    "source": "hostfully-v3",
    "created_at": "2025-06-18T07:50:56.000000Z",
    "updated_at": "2025-06-18T07:53:46.000000Z",
    "property": {
        "title": "Booking Requests"
    }
}
 */

// Filter schema for booking API parameters (same as calendar)
export const bookingParamsSchema = z.object({
    feed_id: z.number().int().describe('Filter property by feed'),
    platform: z.string().nullish().describe('Filter property by platform'),
    property: z.string().describe('Filter property by property slug'),
}).describe('Data filter options for booking');

export type BookingFilter = z.infer<typeof bookingParamsSchema>;


// Price types
export type Prices = {
    cleaning_fee: number;
    damage_protection: number;
    booking_fee: number;
    accommodation_tax: number;
    cleaning_fees_tax: number;
    damage_protection_tax: number;
};

export type PriceCheckParams = {
    guests: number;
    check_in?: string;
    check_out?: string;
};

// Fetch availability (reuses calendar API)
async function fetchAvailability(filter: BookingFilter) {
    const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-calendar`);
    Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
        }
    });
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error('Failed to fetch property availability');
    const data = await res.json();
    return data as AvailabilityDataRecord;
}

// Fetch price data
async function fetchPrice(
    apiUrl: string,
    params: PriceCheckParams
): Promise<{ result: Prices }> {
    const url = new URL(apiUrl);
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
        }
    });
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error("Failed to fetch price");
    const data = (await res.json()) as { result: Prices };
    return data;
}

// React Query hooks
export function useFetchAvailability(filter: BookingFilter) {
    return useQuery({
        queryKey: ['booking-availability', filter],
        queryFn: () => fetchAvailability(filter),
    });
}

export function useFetchPrice(
    apiUrl: string | undefined,
    params: PriceCheckParams,
    enabled: boolean = false
) {
    return useQuery({
        queryKey: ['booking-price', apiUrl, params],
        queryFn: () => {
            if (!apiUrl) throw new Error('Price API URL is not defined');
            return fetchPrice(apiUrl, params);
        },
        enabled: enabled && !!apiUrl && !!params.check_in && !!params.check_out,
    });
}

const upsertQuote = async (params: {
    feed_id: number;
    platform?: string;
    property: string;
    checkin: Date;
    checkout: Date;
    guests: number;
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
    coupon?: string;
    quote_id?: string | null;
}) => {
    const isUpdate = !!params.quote_id
    const targetUrl = isUpdate
        ? `${homerunnerApiBaseUrl()}/public-api/v1/quote/update`
        : `${homerunnerApiBaseUrl()}/public-api/v1/quotes`;
    // Breakdown-aware guest fields: without a breakdown every guest is an
    // adult (previous behavior); infants and pets never count toward `guests`.
    const guestFields = {
        adults: params.adults ?? params.guests,
        ...(params.children !== undefined ? { children: params.children } : {}),
        ...(params.infants !== undefined ? { infants: params.infants } : {}),
        ...(params.pets !== undefined ? { pets: params.pets } : {}),
    };
    const response = await fetch(targetUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({
            ...(isUpdate ? {
                quote_id: params.quote_id,
                feed_id: params.feed_id,
                checkin: DateTime.fromJSDate(params.checkin).toFormat('yyyy-MM-dd'),
                checkout: DateTime.fromJSDate(params.checkout).toFormat('yyyy-MM-dd'),
                ...guestFields,
                coupon: params.coupon,
            } : {
                ...params,
                checkin: DateTime.fromJSDate(params.checkin).toFormat('yyyy-MM-dd'),
                checkout: DateTime.fromJSDate(params.checkout).toFormat('yyyy-MM-dd'),
                ...guestFields,
            }),
        }),
    });
    const data = await response.json();
    if (data.status_code >= 400) {
        throw new Error(extractApiErrorMessage(data, 'Failed to upsert quote'))
    };
    return data as {
        success: boolean;
        status_code: number;
        result: Quote;
    };
}

/**
 * Creates a quote on first call, then updates the existing quote on subsequent calls
 * (when dates, guests, or coupon change). Uses a stable quote_id ref to track the
 * current quote across React Query re-fetches.
 */
export function useUpsertQuote(params: {
    feed_id: number;
    platform?: string;
    property: string;
    checkin: Date;
    checkout: Date;
    guests: number;
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
    coupon?: string;
}, enabled: boolean = true) {
    // A quote id is only meaningful for the property it was created for: the update
    // payload carries no property, so updating across a repoint would put the NEW
    // party on the OLD property's quote — and checkout books by quote id.
    //
    // One SESSION per feed|platform|property scope: the session object carries the
    // quote id and the upsert-ordering chain TOGETHER, and a repoint replaces the
    // whole object. Queued runs capture their session — never a hook-global ref —
    // so a request that outlives a repoint (even an A → B → A cycle, where a
    // scope-equality check would pass again and re-arm a shared ref) can only ever
    // read or write the orphaned session it was born in. A fresh session is also a
    // fresh chain: a hung request from the old property can't block the new one's
    // first quote.
    //
    // Within one session, upserts WRITE in issue order (failures included), so the
    // newest params are always the last server-side write — two quick param
    // changes are two fetches, and an older response landing last would otherwise
    // leave the server on state the visitor never saw. Reading quote_id inside the
    // chained run keeps a create's id visible to the update queued behind it.
    type QuoteSession = { quoteId: string | null; tail: Promise<unknown> };
    const quoteScope = params.feed_id + '|' + (params.platform ?? '') + '|' + params.property;
    const sessionRef = useRef<{ scope: string; session: QuoteSession } | null>(null);
    if (!sessionRef.current || sessionRef.current.scope !== quoteScope) {
        sessionRef.current = { scope: quoteScope, session: { quoteId: null, tail: Promise.resolve() } };
    }
    const session = sessionRef.current.session;

    return useQuery({
        queryKey: ['booking-quote', params],
        queryFn: async () => {
            if (!params.feed_id) throw new Error('Feed ID is required');
            if (!params.property) throw new Error('Property is required');
            if (!params.checkin) throw new Error('Check-in date is required');
            if (!params.checkout) throw new Error('Check-out date is required');
            if (!params.guests) throw new Error('Number of guests is required');

            const run = () => upsertQuote({
                ...params,
                quote_id: session.quoteId,
            });
            const chained = session.tail.then(run, run);
            session.tail = chained.then(() => undefined, () => undefined);
            const result = await chained;

            if (!result.success) throw new Error('Failed to upsert quote');

            // Session-scoped write-back: if the widget was repointed while this
            // request was in flight, this session is orphaned and the write is
            // invisible to the live one.
            session.quoteId = result.result.id;

            return result.result;
        },
        retry: false,
        // Keep the previous quote on screen while a new one is fetched (e.g. applying a
        // coupon changes the query key). Without this the overview unmounts and the
        // coupon input disappears mid-interaction (#627). `isFetching`/`isPlaceholderData`
        // then distinguish "loading a fresh quote" from "showing the last good one".
        placeholderData: keepPreviousData,
        enabled: enabled && !!params.feed_id && !!params.property && !!params.checkin && !!params.checkout && !!params.guests,
    });
}

/**
 * Resolve a property's external-PMS hosted checkout URL (#443). Used to send the guest
 * straight to the PMS's own checkout when the property reserves + pays entirely on the
 * PMS (reservation_target === 'hosted'). Throws on a non-2xx / missing url.
 */
export async function fetchExternalCheckoutUrl(quoteId: string, feedId: number): Promise<string> {
    const response = await fetch(`${homerunnerApiBaseUrl()}/public-api/v1/${quoteId}/checkout-url?feed_id=${feedId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
    });
    const data = await response.json();
    if (data.status_code >= 400) {
        throw new Error(extractApiErrorMessage(data, 'Failed to get checkout URL'));
    }
    const typedData = data as { success: boolean; status_code: number; data: { url: string } };
    if (!typedData.data?.url) throw new Error('No external checkout URL returned');
    return typedData.data.url;
}

export function useGetCheckoutUrl(params: {
    quote_id?: string | null;
    feed_id: number;
}) {
    return useQuery({
        queryKey: ['booking-get-checkout-url', params],
        queryFn: async () => {
            if (!params.quote_id) throw new Error('Quote ID is required');
            return { url: await fetchExternalCheckoutUrl(params.quote_id, params.feed_id) };
        },
        retry: false,
    });
}